import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { PrefKey } from '@standardnotes/snjs';
import { useCallback, useEffect, useState } from 'react';
import { c } from 'ttag';
import IconButton from '@/Components/Button/IconButton';
import usePreference from '@/Hooks/usePreference';
import { getCSSValueFromAlignment, ImageAlignmentOptions } from './ImageAlignmentOptions';
import { ElementIds } from '../../Constants/ElementIDs';
import { getOverflows } from '@/Components/Popover/Utils/Collisions';
const MinimumZoomPercent = 10;
const DefaultZoomPercent = 100;
const MaximumZoomPercent = 1000;
const ZoomPercentModifier = 10;
const PercentageDivisor = 100;
const ImagePreview = ({ objectUrl, isEmbeddedInSuper, imageZoomLevel, setImageZoomLevel, alignment, changeAlignment, }) => {
    const [imageWidth, setImageWidth] = useState(0);
    const [imageHeight, setImageHeight] = useState(0);
    const [imageZoomPercent, setImageZoomPercent] = useState(imageZoomLevel ? imageZoomLevel : DefaultZoomPercent);
    const [isZoomInputVisible, setIsZoomInputVisible] = useState(false);
    useEffect(() => {
        setImageZoomPercent(imageZoomLevel ? imageZoomLevel : DefaultZoomPercent);
    }, [imageZoomLevel]);
    const setImageZoom = useCallback((zoomLevel) => {
        setImageZoomPercent(zoomLevel);
        setImageZoomLevel === null || setImageZoomLevel === void 0 ? void 0 : setImageZoomLevel(zoomLevel);
    }, [setImageZoomLevel]);
    useEffect(() => {
        const image = new Image();
        image.src = objectUrl;
        image.onload = () => {
            setImageWidth(image.width);
            setImageHeight(image.height);
        };
    }, [objectUrl]);
    const widthIfEmbedded = imageWidth * (imageZoomPercent / PercentageDivisor);
    const imageResizer = (_jsxs(_Fragment, { children: [_jsxs("span", { className: "mr-1.5", children: [isEmbeddedInSuper
                        ? c('B7.FilesSubscriptionHelp.Files.Info').t `Size`
                        : c('B7.FilesSubscriptionHelp.Files.Info').t `Zoom`, ":"] }), _jsx(IconButton, { className: "rounded p-1 hover:bg-contrast", icon: 'subtract', title: isEmbeddedInSuper
                    ? c('B7.FilesSubscriptionHelp.Files.Info').t `Decrease size`
                    : c('B7.FilesSubscriptionHelp.Files.Info').t `Zoom Out`, focusable: true, onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const newPercent = imageZoomPercent - ZoomPercentModifier;
                    if (newPercent >= ZoomPercentModifier) {
                        setImageZoom(newPercent);
                    }
                    else {
                        setImageZoom(imageZoomPercent);
                    }
                }, onMouseDown: (e) => {
                    e.preventDefault();
                } }), isZoomInputVisible ? (_jsxs("div", { className: "mx-2", children: [_jsx("input", { type: "number", className: "w-10 bg-default text-center", defaultValue: imageZoomPercent, onKeyDown: (event) => {
                            event.stopPropagation();
                            if (event.key === 'Enter') {
                                const value = parseInt(event.currentTarget.value);
                                if (value >= MinimumZoomPercent && value <= MaximumZoomPercent) {
                                    setImageZoom(value);
                                }
                                setIsZoomInputVisible(false);
                            }
                        }, onBlur: (event) => {
                            setIsZoomInputVisible(false);
                            const value = parseInt(event.currentTarget.value);
                            if (value >= MinimumZoomPercent && value <= MaximumZoomPercent) {
                                setImageZoom(value);
                            }
                        } }), "%"] })) : (_jsxs("button", { className: "mx-1 rounded px-1.5 py-1 hover:bg-contrast", onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsZoomInputVisible((visible) => !visible);
                }, children: [imageZoomPercent, "%"] })), _jsx(IconButton, { className: "rounded p-1 hover:bg-contrast", icon: "add", title: isEmbeddedInSuper
                    ? c('B7.FilesSubscriptionHelp.Files.Info').t `Increase size`
                    : c('B7.FilesSubscriptionHelp.Files.Info').t `Zoom In`, focusable: true, onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setImageZoom(imageZoomPercent + ZoomPercentModifier);
                }, onMouseDown: (e) => {
                    e.preventDefault();
                } })] }));
    const defaultSuperImageAlignment = usePreference(PrefKey.SuperNoteImageAlignment);
    const finalAlignment = alignment || defaultSuperImageAlignment;
    const justifyContent = isEmbeddedInSuper ? getCSSValueFromAlignment(finalAlignment) : 'center';
    return (_jsxs("div", { className: "group relative flex h-full min-h-0 w-full items-center", style: { justifyContent }, onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
        }, children: [_jsx("div", { className: "relative flex h-full w-full items-center justify-center overflow-auto", style: {
                    width: isEmbeddedInSuper ? `${widthIfEmbedded}px` : '',
                    aspectRatio: isEmbeddedInSuper ? `${imageWidth} / ${imageHeight}` : '',
                }, children: _jsx("img", { src: objectUrl, style: {
                        height: isEmbeddedInSuper ? '100%' : `${imageZoomPercent}%`,
                        ...(isEmbeddedInSuper
                            ? {}
                            : imageZoomPercent <= DefaultZoomPercent
                                ? {
                                    minWidth: '100%',
                                    objectFit: 'contain',
                                }
                                : {
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    margin: 'auto',
                                    maxWidth: 'none',
                                }),
                    } }) }), !isEmbeddedInSuper && (_jsx("div", { className: "absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center rounded border border-solid border-border bg-default px-3 py-1", children: imageResizer })), isEmbeddedInSuper && (_jsx("div", { className: "invisible absolute bottom-full left-1/2 z-10 w-max -translate-x-1/2 px-1 pb-1 focus-within:visible group-hover:visible [.embedBlockFocused_&]:visible", ref: (popover) => {
                    const editorRoot = document.getElementById(ElementIds.SuperEditorContent);
                    if (!popover || !editorRoot) {
                        return;
                    }
                    const editorRootRect = editorRoot.getBoundingClientRect();
                    const popoverRect = popover.getBoundingClientRect();
                    const overflows = getOverflows(popoverRect, editorRootRect);
                    if (overflows.top > 0) {
                        popover.style.setProperty('--tw-translate-y', `${overflows.top}px`);
                    }
                }, children: _jsxs("div", { className: "flex divide-x divide-border rounded border border-border bg-default", children: [changeAlignment && (_jsx("div", { className: "flex items-center gap-1 px-1 py-0.5", children: _jsx(ImageAlignmentOptions, { alignment: finalAlignment, changeAlignment: changeAlignment }) })), _jsx("div", { className: "flex items-center px-2 py-0.5 text-sm", children: imageResizer })] }) }))] }));
};
export default ImagePreview;
